import { useState, useEffect, useCallback } from "react";
import { AWARDS_API, fetchWithAuth } from "../utils/api";
import { scoringRulesService } from "../services/scoringRulesService";

export function useAwardsSettings() {
  const [awards, setAwards] = useState([]);
  const [isPercentageBased, setIsPercentageBased] = useState(true);
  const [maxPointsPerJudge, setMaxPointsPerJudge] = useState(100);
  const [numberOfJudges, setNumberOfJudges] = useState(3);
  
  // NEW: Custom awards configuration
  const [customAwards, setCustomAwards] = useState([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [isExistingConfig, setIsExistingConfig] = useState(false); // Track if config exists

  // Calculate total possible points
  const totalPossiblePoints = maxPointsPerJudge * numberOfJudges;

  const fetchAwards = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchWithAuth(AWARDS_API);
      
      console.log('🔍 FETCHED data from API:', JSON.stringify(data, null, 2));
      console.log('🔍 FETCHED customAwards specifically:', JSON.stringify(data.customAwards, null, 2));
      
      // Check if we got actual data or defaults
      const hasExistingData = data.awards && data.awards.length > 0;
      setIsExistingConfig(hasExistingData);
      
      setAwards(data.awards || []);
      setIsPercentageBased(data.isPercentageBased ?? true);
      setMaxPointsPerJudge(data.maxPointsPerJudge || 100);
      setNumberOfJudges(data.numberOfJudges || 3);
      setCustomAwards(data.customAwards || []);
      setHasChanges(false);
    } catch (err) {
      setError(err.message || "Failed to load awards");
      console.error("Error loading awards:", err);
      setIsExistingConfig(false);
    } finally {
      setLoading(false);
    }
  }, []);

  const saveAwards = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setSuccessMessage(null);
      
      // Use POST for new configurations, PUT for updates
      const method = isExistingConfig ? "PUT" : "POST";
      const actionText = isExistingConfig ? "updated" : "created";
      
      console.log(`Using ${method} method for awards configuration (existing: ${isExistingConfig})`);
      console.log('📤 SENDING customAwards:', JSON.stringify(customAwards, null, 2));
      
      const response = await fetchWithAuth(AWARDS_API, {
        method: method,
        body: JSON.stringify({
          awards,
          isPercentageBased,
          maxPointsPerJudge,
          numberOfJudges,
          customAwards
        })
      });
      
      console.log('📥 RECEIVED response:', JSON.stringify(response, null, 2));
      
      // Update local state with the saved data
      if (response) {
        setAwards(response.awards || []);
        setIsPercentageBased(response.isPercentageBased ?? true);
        setMaxPointsPerJudge(response.maxPointsPerJudge || 100);
        setNumberOfJudges(response.numberOfJudges || 3);
        setCustomAwards(response.customAwards || []);
        setIsExistingConfig(true); // Now we have an existing config
      }
      
      setHasChanges(false);
      setSuccessMessage(`Awards configuration ${actionText} successfully!`);
      
      // Clear scoring rules cache so competitions get updated rules
      scoringRulesService.clearCache();
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
      
      return true;
    } catch (err) {
      setError(err.message || "Failed to save awards");
      console.error("Error saving awards:", err);
      return false;
    } finally {
      setLoading(false);
    }
  }, [awards, isPercentageBased, maxPointsPerJudge, numberOfJudges, customAwards, isExistingConfig]);

  const addAward = useCallback(() => {
    const newAward = {
      id: Date.now(),
      name: "",
      maxScore: isPercentageBased ? 100 : totalPossiblePoints,
      minScore: isPercentageBased ? 97.5 : Math.round(totalPossiblePoints * 0.975),
      color: "#" + Math.floor(Math.random()*16777215).toString(16)
    };
    setAwards(prev => [...prev, newAward]);
    setHasChanges(true);
    setSuccessMessage(null); // Clear success message when making changes
  }, [isPercentageBased, totalPossiblePoints]);

  const updateAward = useCallback((id, field, value) => {
    setAwards(prev => prev.map(award => 
      award.id === id ? { ...award, [field]: value } : award
    ));
    setHasChanges(true);
    setSuccessMessage(null); // Clear success message when making changes
  }, []);

  const removeAward = useCallback((id) => {
    setAwards(prev => prev.filter(award => award.id !== id));
    setHasChanges(true);
    setSuccessMessage(null); // Clear success message when making changes
  }, []);

  const toggleScoringMode = useCallback((usePercentage) => {
    if (usePercentage === isPercentageBased) return;

    // Convert existing awards
    const convertedAwards = awards.map(award => ({
      ...award,
      maxScore: usePercentage 
        ? (award.maxScore / totalPossiblePoints) * 100
        : (award.maxScore / 100) * totalPossiblePoints,
      minScore: usePercentage
        ? (award.minScore / totalPossiblePoints) * 100
        : (award.minScore / 100) * totalPossiblePoints
    }));

    setIsPercentageBased(usePercentage);
    setAwards(convertedAwards);
    setHasChanges(true);
  }, [awards, isPercentageBased, totalPossiblePoints]);

  const updateMaxPointsPerJudge = useCallback((value) => {
    setMaxPointsPerJudge(value);
    setHasChanges(true);
    setSuccessMessage(null); // Clear success message when making changes
  }, []);

  const updateNumberOfJudges = useCallback((value) => {
    setNumberOfJudges(value);
    setHasChanges(true);
    setSuccessMessage(null); // Clear success message when making changes
  }, []);

  // NEW: Custom Awards Management
  const addCustomAward = useCallback((customAward) => {
    setCustomAwards(prev => [...prev, customAward]);
    setHasChanges(true);
    setSuccessMessage(null);
  }, []);

  const updateCustomAward = useCallback((index, updatedAward) => {
    setCustomAwards(prev => prev.map((award, i) => 
      i === index ? updatedAward : award
    ));
    setHasChanges(true);
    setSuccessMessage(null);
  }, []);

  const removeCustomAward = useCallback((index) => {
    setCustomAwards(prev => prev.filter((_, i) => i !== index));
    setHasChanges(true);
    setSuccessMessage(null);
  }, []);

  const toggleCustomAwardEnabled = useCallback((index) => {
    setCustomAwards(prev => prev.map((award, i) => 
      i === index ? { ...award, enabled: !award.enabled } : award
    ));
    setHasChanges(true);
    setSuccessMessage(null);
  }, []);

  useEffect(() => {
    const abortController = new AbortController();
    
    const fetchAwardsWithAbort = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await fetchWithAuth(AWARDS_API, { signal: abortController.signal });
        
        // Only update state if the request wasn't aborted
        if (!abortController.signal.aborted) {
          console.log('🔍 FETCHED data from API:', JSON.stringify(data, null, 2));
          console.log('🔍 FETCHED customAwards specifically:', JSON.stringify(data.customAwards, null, 2));
          
          // Check if we got actual data from the database (not just defaults)
          // If there's a createdAt or updatedAt field, it came from the database
          const hasExistingData = data && (data.createdAt || data.updatedAt);
          setIsExistingConfig(hasExistingData);
          
          setAwards(data.awards || []);
          setIsPercentageBased(data.isPercentageBased ?? true);
          setMaxPointsPerJudge(data.maxPointsPerJudge || 100);
          setNumberOfJudges(data.numberOfJudges || 3);
          setCustomAwards(data.customAwards || []);
          setHasChanges(false);
        }
      } catch (err) {
        // Only set error if the request wasn't aborted
        if (!abortController.signal.aborted) {
          setError(err.message || "Failed to load awards");
          console.error("Error loading awards:", err);
          setIsExistingConfig(false);
        }
      } finally {
        // Only update loading state if the request wasn't aborted
        if (!abortController.signal.aborted) {
          setLoading(false);
        }
      }
    };
    
    fetchAwardsWithAbort();
    
    // Cleanup function to abort the request if component unmounts or effect re-runs
    return () => {
      abortController.abort();
    };
  }, []); // Remove fetchAwards dependency to prevent unnecessary re-runs

  return {
    awards,
    isPercentageBased,
    maxPointsPerJudge,
    numberOfJudges,
    totalPossiblePoints,
    loading,
    error,
    hasChanges,
    successMessage,
    fetchAwards,
    saveAwards,
    addAward,
    updateAward,
    removeAward,
    toggleScoringMode,
    updateMaxPointsPerJudge,
    updateNumberOfJudges,
    // Custom awards
    customAwards,
    addCustomAward,
    updateCustomAward,
    removeCustomAward,
    toggleCustomAwardEnabled
  };
} 